import type { QuestionData } from "../types/survey.types";
import type { SurveyQuestionType } from "./questionTypes";
import { US_PHONE_COUNTRY_CODE, US_PHONE_FORMAT } from "./phoneQuestion.constants";

type QuestionDataDefaults = Partial<QuestionData>;
type QuestionDataDefaultFactory = () => QuestionDataDefaults;
type QuestionOptionDefaults = NonNullable<QuestionData["options"]>;
type GridRowDefaults = NonNullable<QuestionData["rows"]>;
type GridColumnDefaults = NonNullable<QuestionData["columns"]>;

const DEFAULT_OPTION: QuestionOptionDefaults[number] = {
  id: "opt1",
  text: "",
  value: "",
  order: 1,
  is_other: false,
};

const DEFAULT_GRID_ROWS: GridRowDefaults = [
  { id: "row1", text: "Row 1", order: 1 },
  { id: "row2", text: "Row 2", order: 2 },
];

const DEFAULT_GRID_COLUMNS: GridColumnDefaults = [
  { id: "col1", text: "Column 1", order: 1 },
  { id: "col2", text: "Column 2", order: 2 },
];

const cloneDefaultOption = (): QuestionOptionDefaults[number] => ({ ...DEFAULT_OPTION });
const cloneDefaultGridRows = (): GridRowDefaults => DEFAULT_GRID_ROWS.map((row) => ({ ...row }));
const cloneDefaultGridColumns = (): GridColumnDefaults =>
  DEFAULT_GRID_COLUMNS.map((column) => ({ ...column }));

const createChoiceDefaults = (): QuestionDataDefaults => ({
  options: [cloneDefaultOption()],
  allow_other: false,
});

const createGridDefaults = (): QuestionDataDefaults => ({
  rows: cloneDefaultGridRows(),
  columns: cloneDefaultGridColumns(),
});

const QUESTION_DATA_DEFAULT_FACTORIES = {
  short_answer: () => ({ max_length: 100, min_length: 1 }),
  long_answer: () => ({ max_length: 1000, min_length: 10 }),
  multiple_choice: createChoiceDefaults,
  multiple_answer: () => ({
    ...createChoiceDefaults(),
    min_select: 1,
    max_select: 3,
  }),
  rating: () => ({ min_rating: 1, max_rating: 5 }),
  scale: () => ({ min_scale: 1, max_scale: 10, min_label: "Disagree", max_label: "Agree" }),
  date: () => ({ min_date: "1900-01-01", max_date: "2100-12-31" }),
  time: () => ({ min_time: "00:00", max_time: "23:59" }),
  datetime: () => ({
    min_datetime: new Date().toISOString(),
    max_datetime: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
  }),
  file_upload: () => ({
    max_file_size: 5242880,
    allowed_formats: ["jpg", "jpeg", "png", "pdf"],
    max_files: 1,
  }),
  email: () => ({ allow_multiple: false }),
  phone: () => ({ country_code: US_PHONE_COUNTRY_CODE, format: US_PHONE_FORMAT }),
  number: () => ({ min_value: 0, max_value: 1000000, decimal_places: 0 }),
  yes_no: () => ({ yes_label: "Yes", no_label: "No" }),
  dropdown: () => ({
    ...createChoiceDefaults(),
    placeholder: "Select an option",
  }),
  multiple_choice_grid: createGridDefaults,
  checkbox_grid: createGridDefaults,
} satisfies Record<SurveyQuestionType, QuestionDataDefaultFactory>;

export const getDefaultQuestionDataForType = (type: SurveyQuestionType): QuestionDataDefaults =>
  QUESTION_DATA_DEFAULT_FACTORIES[type]();
